// The page registry — the shared vocabulary for the `pages` and `tabs` grant
// axes, imported by BOTH the SPA and the API.
//
// Design of record: context/backlog/authorization-system-rebuild.md § Q23,
// Q29, Q31. Read it before changing the shape here; the reasoning is not
// restated.
//
// ── What this is, and what it deliberately is NOT ─────────────────────
//
// ⚠️ **This registry names DESTINATIONS, not routes.** It is deliberately
// SMALLER than the SPA's ~60 `<Route>` elements, and a route with no entry
// here is not a gap:
//
//   - **Drill-downs INHERIT their parent page.** The ~10 destinations under
//     `/commissions/:runId` (`customer/:customerId/lines`,
//     `order/:orderId/tree/…`, the four drill grids) get no id of their own.
//     The API is already this shape — all 8 `commission-run-detail.ts` routes
//     share one privilege — so splitting the SPA finer than the API gates buys
//     nothing. Same for `/quality/rules/:ruleId`, `/quality/issues/:issueId`
//     and `/system/engine/runs/:requestId`.
//   - **Redirect-only routes get no entry** (`/ap` → `/ap/bills`, a tab
//     layout's `index` when it only redirects).
//
// ── `nav` ────────────────────────────────────────────────────────────
//
// `nav` is the sidebar LABEL, and its PRESENCE is the flag — there is no
// boolean that could fall out of sync with the label. Most entries have no
// `nav`: every drill-down is reachable-but-unlisted, and the sidebar items are
// the exception. The SPA renders the sidebar as:
//
//     PAGES.filter(p => p.nav && grants.pages.includes(p.id))
//
// Whether a page appears in the sidebar is a property of the PAGE, not of the
// role — which is the whole reason the `nav` grant axis was deleted (Q23). Two
// lists could only ever disagree, with nothing to catch it.
//
// ── `tabs` ───────────────────────────────────────────────────────────
//
// **Tabs are BARE here** (`['summary', 'orders']`). The loader qualifies them
// as `<pageId>.<tab>` when it checks `roles.jsonc`, because the page id is
// already on the entry and repeating it would only be a chance to drift.
// Qualification matters: `timeline` is a tab on both `customers.detail` and
// `orders.detail`, so a bare id is ambiguous — and resolving an ambiguous id
// flatly is exactly the section-collision bypass that already shipped once on
// the field axes.
//
// A granted page with zero granted tabs renders honestly ("no tabs available
// for your role"), never a blank outlet, and is a legal configuration (Q19).
//
// ── Local-only surfaces ARE named here ───────────────────────────────
//
// Several entries below are for surfaces that are NOT deployed post-cutover
// (§ Scope). They are named anyway, so that returning one to a deployed
// environment is a CONFIGURATION change (grant the page) rather than a code
// change. Nothing is being removed; the axis is deployment, not removal.
//
// ⚠️ The scope cut is by **UI SURFACE**, not by route path, and it cannot be
// derived from this file or from the codebase. Do not try.
export const PAGES = [
    // ── Customers ──────────────────────────────────────────────────────
    { id: 'customers.list', path: '/customers', nav: 'Customers' },
    {
        id: 'customers.detail',
        path: '/customers/:id',
        // 14 tabs, matching customer-layout.tsx's TABS. `summary` is the index
        // route (`to: '.'`), so it has no URL segment of its own.
        //
        // Q29: `coding`, `commission_history` and `volumes` are THREE separate tab
        // grants, though they share `customer.commissions:view` today. Splitting
        // later is cheap; merging is not.
        tabs: [
            'summary',
            'downline',
            'orders',
            'timeline',
            'commission_overrides',
            'coding',
            'commission_history',
            'volumes',
            'payments',
            'documents',
            'site',
            'quality',
            'raw',
        ],
        // ⚠️ ONE id so far, and deliberately so — see PageEntry.lists. `notes` is
        // here because it has exactly one projection and Craig picked it
        // (2026-08-16). Every other collection on this page waits for its own pick.
        lists: ['notes'],
    },
    {
        id: 'orders.detail',
        path: '/customers/:id/orders/:orderId',
        // `invoice` is the index route. `timeline` collides by NAME with
        // customers.detail's — which is the case that forces qualification.
        tabs: ['invoice', 'commissions', 'timeline'],
    },
    // ── Commissions ────────────────────────────────────────────────────
    { id: 'commissions.list', path: '/commissions', nav: 'Commissions' },
    {
        id: 'commissions.run',
        path: '/commissions/:runId',
        // `summary` is the index route. The ten drill destinations under this
        // path inherit this page and get no id — see the header.
        tabs: ['summary', 'logs'],
    },
    // ── Reports ────────────────────────────────────────────────────────
    //
    // Coarse for MVP — one grant for the whole runner, per-report granularity is
    // a future ask (§ Scope corrections), not a blocker.
    { id: 'reports', path: '/reports', nav: 'Reports' },
    // ── Bulk operations ────────────────────────────────────────────────
    { id: 'bulk_operations', path: '/bulk-operations', nav: 'Bulk Operations' },
    // ── Payout Tool ────────────────────────────────────────────────────
    //
    // Split by product decision: Batches and EMEA are deployed; Bills, Payouts
    // and EMEA update are speculative build pending business review. The `/ap`
    // path itself only redirects, so it gets no entry.
    //
    // ⚠️ `nav` MOVED from `ap.bills` to `ap.batches` (2026-08-17), and it had to.
    // The label used to sit on `ap.bills` because that is where `/ap` redirects —
    // which was harmless while the sidebar was a hardcoded list, and became a
    // disappearing link the moment the SPA started rendering the sidebar from the
    // pages a role actually holds: `ap.bills` is local-only, so NO role holds it,
    // so the Payout Tool link vanished for everyone including Admin.
    //
    // The deployed entry carries the label instead. Consequence, and it is the
    // intended one: the sidebar link now lands on Batches rather than Bills.
    { id: 'ap.bills', path: '/ap/bills' },
    { id: 'ap.payouts', path: '/ap/payouts' },
    { id: 'ap.batches', path: '/ap/batches', nav: 'Payout Tool' },
    { id: 'ap.batch_detail', path: '/ap/batches/:reference/:payoutDate' },
    { id: 'ap.emea', path: '/ap/emea' },
    { id: 'ap.emea_update', path: '/ap/emea-update' },
    // ── Operator tooling ───────────────────────────────────────────────
    {
        id: 'system.migration_sync',
        path: '/system/migration-sync',
        nav: 'Migration sync',
        tabs: ['overview', 'plans', 'tables', 'activity', 'db'],
    },
    {
        id: 'system.migration_sync_plan',
        path: '/system/migration-sync/plans/:planId',
    },
    // The Temporal Schedule of every data-maintenance job. Read-only.
    { id: 'system.schedules', path: '/system/schedules', nav: 'Schedules' },
    // ═══ Local-only from here down ═════════════════════════════════════
    //
    // Registry ORDER is sidebar order — `Sidebar` filters PAGES and does not
    // sort — so grouping these last keeps the deployed surface at the top of
    // the nav and everything reachable only through the `Local` role beneath
    // it. Nothing enforces the split; moving an entry across this line is a
    // reordering, and the grant in roles.jsonc is what actually decides reach.
    // ── Payments ───────────────────────────────────────────────────────
    { id: 'payments.list', path: '/payments', nav: 'Payments' },
    { id: 'payments.run', path: '/payments/:runId' },
    // ── Input review ───────────────────────────────────────────────────
    //
    // The BULK review page drops (§ Scope, settled 2026-08-16). The
    // per-customer surface it duplicates stays, as `customers.detail`'s
    // `commission_overrides` tab.
    {
        id: 'reviews',
        path: '/reviews',
        nav: 'Input review',
        tabs: ['customer_overrides', 'adjustments', 'manual_qualifiers'],
    },
    // ── Global Changelog ───────────────────────────────────────────────
    //
    // Every `app.*_change_log` table unioned into one filterable feed: who made
    // the change, who was changed, when, and what. Sits directly BELOW Input
    // review in the sidebar, which is what this position in the registry means —
    // order here IS sidebar order.
    //
    // READ ONLY, and granted narrowly (Level5, SuperUser, SysAdmin): the feed is
    // unscoped, so it shows edits to customers a role would not otherwise reach,
    // and the `detail` text quotes the values that changed. Reaching the page is
    // not the same as reading those values — `auth/change-log-mask.ts` decides
    // that separately, per section, on the same units as the detail route.
    { id: 'changelog', path: '/changelog', nav: 'Global Changelog' },
    // ── Data quality ───────────────────────────────────────────────────
    //
    // `/quality/rules/:ruleId` and `/quality/issues/:issueId` inherit this page.
    { id: 'quality', path: '/quality', nav: 'Data quality' },
    // `/system/engine/runs/:requestId` and its tree drill inherit this page.
    { id: 'system.engine', path: '/system/engine', nav: 'Commission engine' },
    // In-app bug reports. `/system/bug-reports/:bugId` inherits this
    // page — it is the same destination, drilled. Deleting one is the separate
    // `delete_bug_report` operation.
    //
    // ⚠️ A capture carries whatever was on the operator's screen, so this page
    // shows customer PII by construction (local and preview run unmasked). It is
    // granted to SysAdmin alone, and that is the reason.
    { id: 'system.bug_reports', path: '/system/bug-reports', nav: 'Bug Reports' },
    { id: 'system.user_roles', path: '/system/user-roles', nav: 'User Roles' },
];
/** Page ids, for the loader's membership checks. */
export const PAGE_IDS = new Set(PAGES.map((p) => p.id));
/**
 * Every legal tab grant, QUALIFIED as `<pageId>.<tab>`. This is the vocabulary
 * `roles.jsonc` writes against — the registry's bare tabs are an authoring
 * convenience, never the grant name.
 */
export const TAB_IDS = new Set(PAGES.flatMap((p) => (p.tabs ?? []).map((t) => `${p.id}.${t}`)));
/** The page a qualified tab id belongs to, or undefined if it is not a legal tab. */
export function pageOfTab(qualifiedTab) {
    for (const p of PAGES) {
        for (const t of p.tabs ?? []) {
            if (`${p.id}.${t}` === qualifiedTab)
                return p.id;
        }
    }
    return undefined;
}
/**
 * Every legal LIST grant, QUALIFIED as `<pageId>.<list>` — the same shape and
 * the same reason as TAB_IDS.
 *
 * A list grant answers "may you ENUMERATE this projection?", which is a
 * question about a caller and a destination, so it needs no row and is
 * refusable before any query runs. That is what keeps Q18 intact: a denied
 * caller gets a 403, never a masked payload, so lists still project only
 * `open` fields and still buy "no masking, no leak analysis on lists".
 */
export const LIST_IDS = new Set(PAGES.flatMap((p) => (p.lists ?? []).map((l) => `${p.id}.${l}`)));
/** The page a qualified list id belongs to, or undefined if it is not a legal list. */
export function pageOfList(qualifiedList) {
    for (const p of PAGES) {
        for (const l of p.lists ?? []) {
            if (`${p.id}.${l}` === qualifiedList)
                return p.id;
        }
    }
    return undefined;
}
//# sourceMappingURL=pages.js.map